<script setup lang="ts">
import { computed } from 'vue';
import CloseIcon from '../../assets/close.svg?component';

export interface TagProps {
  /**
   * 标签颜色
   * 预设颜色: 'purple' | 'success' | 'warning' | 'error'
   * 或自定义颜色值（如 '#ff0000'）
   */
  color?: 'purple' | 'success' | 'warning' | 'error' | string;
  /**
   * 标签变体
   * 'filled': 填充样式（默认）
   * 'outline': 边框样式
   */
  variant?: 'filled' | 'outline';
  /**
   * 是否可关闭
   */
  closable?: boolean;
  /**
   * 自定义关闭图标
   */
  closeIcon?: any;
  /**
   * 是否可见（已废弃，使用 v-show 代替）
   * @deprecated
   */
  visible?: boolean;
  /**
   * 自定义类名
   */
  class?: string;
  /**
   * 点击事件
   */
  onClick?: (e: MouseEvent) => void;
}

const props = withDefaults(defineProps<TagProps>(), {
  variant: 'filled',
  closable: false,
  visible: undefined,
});

const emit = defineEmits<{
  (event: 'close', e: MouseEvent): void;
  (event: 'click', e: MouseEvent): void;
  (event: 'update:visible', visible: boolean): void;
}>();

// 预设颜色列表
const presetColors = ['purple', 'success', 'warning', 'error'] as const;
type PresetColor = (typeof presetColors)[number];

// 判断是否为预设颜色
const isPresetColor = computed(() => {
  if (!props.color) return false;
  return presetColors.includes(props.color as PresetColor);
});

// 判断是否为自定义颜色
const isCustomColor = computed(() => {
  if (!props.color) return false;
  return !isPresetColor.value;
});

// 标签样式类
const tagClasses = computed(() => {
  const classes: string[] = ['x-tag'];

  if (props.variant === 'outline') {
    classes.push('x-tag--outline');
  }

  if (isPresetColor.value) {
    classes.push(`x-tag--${props.color}`);
  }

  if (isCustomColor.value) {
    classes.push('x-tag--has-color');
  }

  if (props.onClick) {
    classes.push('x-tag--clickable');
  }

  if (props.class) {
    classes.push(props.class);
  }

  return classes.join(' ');
});

// 标签样式（自定义颜色时使用）
const tagStyle = computed(() => {
  if (isCustomColor.value && props.color) {
    return {
      backgroundColor: props.color,
      borderColor: 'transparent',
      color: '#fff',
    };
  }
  return {};
});

// 处理关闭点击
const handleCloseClick = (e: MouseEvent) => {
  e.stopPropagation();
  emit('update:visible', false);
  emit('close', e);
};

// 处理标签点击
const handleClick = (e: MouseEvent) => {
  if (props.onClick) {
    props.onClick(e);
  }
  emit('click', e);
};
</script>

<template>
  <span :class="tagClasses" :style="tagStyle" @click="handleClick">
    <slot name="icon" />
    <span v-if="$slots.icon" class="x-tag__content">
      <slot />
    </span>
    <template v-else>
      <slot />
    </template>
    <component
      v-if="closable"
      :is="closeIcon || CloseIcon"
      class="x-tag__close-icon"
      @click="handleCloseClick"
    />
  </span>
</template>

<style scoped lang="scss">
.x-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  border-radius: var(--x-radius-small);
  background-color: var(--x-back-3);
  font-weight: var(--x-weight-medium);
  color: var(--x-text-1);
  white-space: nowrap;
  transition: all 0.2s ease;
  cursor: default;
  border: 1px solid transparent;

  &__content {
    display: inline-flex;
    align-items: center;
  }

  &__close-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    cursor: pointer;
    color: var(--x-text-5);
    transition: color 0.2s ease;
    flex-shrink: 0;

    &:hover {
      color: var(--x-text-1);
    }

    :deep(svg) {
      width: 12px;
      height: 12px;
    }
  }

  // 边框样式变体
  &.x-tag--outline {
    background-color: transparent;
    font-weight: var(--x-weight-regular);
    padding: 2px 5px;
    border-radius: var(--x-radius-medium);

    // 默认边框样式（无颜色时）
    &:not(.x-tag--purple):not(.x-tag--success):not(.x-tag--warning):not(.x-tag--error):not(
        .x-tag--has-color
      ) {
      color: var(--x-text-7);
      border-color: var(--x-back-4);
    }
  }

  // 预设颜色样式
  &.x-tag--purple {
    background-color: var(--x-purple-2);
    color: var(--x-purple-6);

    .x-tag__close-icon {
      color: var(--x-purple-6);

      &:hover {
        color: var(--x-purple-7);
      }
    }

    // 边框样式变体
    &.x-tag--outline {
      background-color: transparent;
      border-color: var(--x-purple-6);
      color: var(--x-purple-6);
    }
  }

  &.x-tag--success {
    background-color: var(--x-success-1);
    color: var(--x-success-6);

    .x-tag__close-icon {
      color: var(--x-success-6);

      &:hover {
        color: var(--x-success-7);
      }
    }

    // 边框样式变体
    &.x-tag--outline {
      background-color: transparent;
      border-color: var(--x-success-7);
      color: var(--x-success-7);
    }
  }

  &.x-tag--warning {
    background-color: var(--x-warning-2);
    color: var(--x-warning-7);

    .x-tag__close-icon {
      color: var(--x-warning-6);

      &:hover {
        color: var(--x-warning-7);
      }
    }

    // 边框样式变体
    &.x-tag--outline {
      background-color: transparent;
      border-color: var(--x-warning-7);
      color: var(--x-warning-7);
    }
  }

  &.x-tag--error {
    background-color: var(--x-error-2);
    color: var(--x-error-6);

    .x-tag__close-icon {
      color: var(--x-error-6);

      &:hover {
        color: var(--x-error-7);
      }
    }

    // 边框样式变体
    &.x-tag--outline {
      background-color: transparent;
      border-color: var(--x-error-6);
      color: var(--x-error-6);
    }
  }

  // 自定义颜色样式
  &.x-tag--has-color {
    border-color: transparent;

    .x-tag__close-icon {
      color: rgba(255, 255, 255, 0.85);

      &:hover {
        color: #fff;
      }
    }
  }

  // 可点击样式
  &.x-tag--clickable {
    cursor: pointer;
  }
}
</style>
